import type { MouseEvent } from 'react';

import {
  AccessTime,
  OpenInNewRounded,
  Star,
  StarBorder,
} from '@mui/icons-material';
import {
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  QueryClient,
  useIsMutating,
  useMutation,
  useQueryClient,
} from 'react-query';
import { useAuthContext } from 'src/context/AuthContextProvider';
import { addFavorite, removeFavorite } from 'src/services/favorite';

import TimelineFutureEventIndicator from './TimelineFutureEventIndicator';
import { fetchTimelineData } from '../services';
import { isTimelineFutureEvent } from '../timelineCalendarDates';
import {
  formatTimelineDate,
  getTimelineOperationPath,
  getTimelineRecordSummary,
  getTimelineRecordTitle,
} from '../timelinePresentation';

import type { Theme } from '@mui/material/styles';
import type { TimelineRecord } from 'src/types/convocation-summary';

type TimelineCachedList = {
  criteriaKey: string;
  pageIndex: number;
  pageSize: number;
  response: Awaited<ReturnType<typeof fetchTimelineData>>;
};

type FavoriteMutationVariables = {
  userId: number;
  operationId: number;
  wasFavorite: boolean;
  favoriteId: number | null;
};

const pendingFavoriteRefetch = new WeakMap<QueryClient, Set<number>>();

const isUserTimelineList = (key: unknown, userId: number) =>
  Array.isArray(key) && key[0] === 'timeline-data' && key[3] === userId;

const hasFavoritesFilter = (filters: unknown) =>
  typeof filters === 'object' &&
  filters !== null &&
  'favorites' in filters &&
  (filters as { favorites?: unknown }).favorites === true;

const isUserFavoritesTimelineQuery = (key: unknown, userId: number) =>
  Array.isArray(key) &&
  key[3] === userId &&
  ((key[0] === 'timeline-data' && key[7] === true) ||
    (key[0] === 'timeline-count' && hasFavoritesFilter(key[4])));

const updateOperationFavorite = (
  cached: TimelineCachedList | undefined,
  operationId: number,
  favorite: TimelineRecord['favorite'],
) => {
  if (!cached?.response?.data) return cached;
  return {
    ...cached,
    response: {
      ...cached.response,
      data: cached.response.data.map((item) =>
        item.operationNumericId === operationId ? { ...item, favorite } : item,
      ),
    },
  };
};

const timelineMarketChipSx = (theme: Theme) => ({
  backgroundColor: theme.palette.primary.main,
  borderColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
});

const timelineFutureChipSx = (theme: Theme) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  borderColor: alpha(theme.palette.primary.main, 0.35),
  color: theme.palette.text.primary,
  '& .MuiChip-icon': { color: theme.palette.primary.main },
});

const timelineNeutralChipSx = {
  backgroundColor: 'background.paper',
  borderColor: 'grey.400',
  color: 'text.primary',
};

interface TimelineCardProps {
  record: TimelineRecord;
  onOpenDetails: (record: TimelineRecord) => void;
  operationScoped?: boolean;
}

export default function TimelineCard({
  record,
  onOpenDetails,
  operationScoped = false,
}: TimelineCardProps) {
  const title = getTimelineRecordTitle(record);
  const operationName = record.operationName?.trim();
  const operationPath = getTimelineOperationPath({
    market: record.mercado,
    slug: record.operationSlug,
    hashId: record.operationid,
  });
  const summary = getTimelineRecordSummary(record.resumo, record.pontos);
  const hasSummary = Boolean(summary);
  const isFutureEvent = isTimelineFutureEvent(record);
  const primarySubject = record.assunto?.trim().toLowerCase();
  const seenSecondarySubjects = new Set<string>();
  const secondarySubjects = (record.assuntosSecundarios || '')
    .split(',')
    .map((subject) => subject.trim())
    .filter((subject) => {
      const normalizedSubject = subject.toLowerCase();
      if (
        !subject ||
        normalizedSubject === primarySubject ||
        seenSecondarySubjects.has(normalizedSubject)
      ) {
        return false;
      }

      seenSecondarySubjects.add(normalizedSubject);
      return true;
    })
    .join(', ');
  const documentClassification = [
    record.categoriadocumento,
    record.tipodocumento,
    record.especiedocumento,
  ]
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value))
    .join(' · ');
  const { auth } = useAuthContext();
  const queryClient = useQueryClient();
  const isFavorite = Boolean(record.favorite?.isFavorite);
  const favoriteId = record.favorite?.id;
  const operationNumericId = record.operationNumericId;
  const favoriteMutationKey = [
    'timeline-favorite',
    auth.user?.id,
    operationNumericId,
  ];
  const isOperationMutating =
    useIsMutating({ mutationKey: favoriteMutationKey, exact: true }) > 0;
  const canToggleFavorite =
    auth.isLoggedIn &&
    Number.isSafeInteger(auth.user?.id) &&
    Number.isSafeInteger(operationNumericId) &&
    Number(operationNumericId) > 0 &&
    (!isFavorite ||
      (Number.isSafeInteger(favoriteId) && Number(favoriteId) > 0));
  const favoriteMutation = useMutation(
    ({
      userId,
      operationId,
      wasFavorite,
      favoriteId: id,
    }: FavoriteMutationVariables) =>
      wasFavorite
        ? removeFavorite(id as number)
        : addFavorite({
            data: {
              user: userId,
              operation: operationId,
            },
          }),
    {
      mutationKey: favoriteMutationKey,
      onMutate: async ({ userId, operationId, wasFavorite }) => {
        await queryClient.cancelQueries({
          predicate: (query) => isUserTimelineList(query.queryKey, userId),
        });
        const snapshots = queryClient.getQueriesData<TimelineCachedList>({
          predicate: (query) => isUserTimelineList(query.queryKey, userId),
        });
        snapshots.forEach(([key]) => {
          queryClient.setQueryData<TimelineCachedList | undefined>(
            key,
            (cached) =>
              updateOperationFavorite(cached, operationId, {
                isFavorite: !wasFavorite,
                id: null,
              }),
          );
        });
        return { snapshots };
      },
      onError: (_error, { operationId }, context) => {
        context?.snapshots.forEach(([key, snapshot]) => {
          if (!snapshot?.response?.data) return;
          queryClient.setQueryData<TimelineCachedList | undefined>(
            key,
            (cached) => {
              if (!cached) return cached;
              const previousFavorites = new Map(
                snapshot.response.data
                  .filter((item) => item.operationNumericId === operationId)
                  .map((item) => [item.id, item.favorite]),
              );
              return {
                ...cached,
                response: {
                  ...cached.response,
                  data: cached.response.data.map((item) =>
                    item.operationNumericId === operationId &&
                    previousFavorites.has(item.id)
                      ? { ...item, favorite: previousFavorites.get(item.id) }
                      : item,
                  ),
                },
              };
            },
          );
        });
      },
      onSuccess: (response, { userId, operationId, wasFavorite }) => {
        if (!wasFavorite) {
          const createdId = response?.data?.id;
          if (Number.isSafeInteger(createdId) && createdId > 0) {
            queryClient
              .getQueriesData<TimelineCachedList>({
                predicate: (query) =>
                  isUserTimelineList(query.queryKey, userId),
              })
              .forEach(([key]) => {
                queryClient.setQueryData<TimelineCachedList | undefined>(
                  key,
                  (cached) =>
                    updateOperationFavorite(cached, operationId, {
                      isFavorite: true,
                      id: createdId,
                    }),
                );
              });
          }
        }
        const users =
          pendingFavoriteRefetch.get(queryClient) ?? new Set<number>();
        users.add(userId);
        pendingFavoriteRefetch.set(queryClient, users);
      },
      onSettled: (_response, _error, { userId }) => {
        const users = pendingFavoriteRefetch.get(queryClient);
        if (
          queryClient.isMutating({
            mutationKey: ['timeline-favorite', userId],
          }) === 1 &&
          users?.delete(userId)
        ) {
          void queryClient.invalidateQueries({
            predicate: (query) =>
              isUserFavoritesTimelineQuery(query.queryKey, userId),
          });
        }
      },
    },
  );
  const favoriteTooltip = !auth.isLoggedIn
    ? 'Entre na sua conta para favoritar a operação.'
    : !auth.user?.id
      ? 'Não foi possível identificar sua conta.'
      : isOperationMutating
        ? 'Atualizando favorito…'
        : !operationNumericId || (isFavorite && !favoriteId)
          ? 'Favorito indisponível para esta operação.'
          : favoriteMutation.isError
            ? 'Não foi possível atualizar o favorito. Tente novamente.'
            : isFavorite
              ? 'Remover dos meus favoritos'
              : 'Adicionar aos meus favoritos';
  const handleFavoriteClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const userId = auth.user?.id;
    if (
      canToggleFavorite &&
      typeof userId === 'number' &&
      typeof operationNumericId === 'number' &&
      queryClient.isMutating({
        mutationKey: favoriteMutationKey,
        exact: true,
      }) === 0
    ) {
      favoriteMutation.mutate({
        userId,
        operationId: operationNumericId,
        wasFavorite: isFavorite,
        favoriteId: favoriteId ?? null,
      });
    }
  };
  const handleCardClick = (event: MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement | null;

    if (
      target?.closest(
        '[data-timeline-card-interactive], a, button, input, select, textarea, [role="button"], [role="link"]',
      )
    ) {
      return;
    }

    event.currentTarget.focus();
    onOpenDetails(record);
  };

  return (
    <Card
      component="article"
      data-testid="timeline-card"
      aria-label={title ? 'Documento ' + title : undefined}
      tabIndex={-1}
      onClick={handleCardClick}
      variant="outlined"
      sx={(theme) => ({
        position: 'relative',
        cursor: 'pointer',
        border: '1px solid',
        borderColor: theme.palette.grey[300],
        borderLeftWidth: 3,
        borderLeftColor: alpha(theme.palette.grey[400], 0.6),
        borderRadius: 2,
        boxShadow: theme.shadows[1],
        transition: theme.transitions.create([
          'border-left-color',
          'box-shadow',
        ]),
        '&:hover': {
          borderLeftColor: theme.palette.grey[600],
          boxShadow: theme.shadows[4],
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: theme.palette.primary.main,
          outlineOffset: 2,
        },
      })}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Stack spacing={1.5}>
          <Stack
            direction="row"
            flexWrap="wrap"
            justifyContent="space-between"
            alignItems="center"
            gap={1}>
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              flexWrap="wrap"
              sx={{ '& .MuiChip-label': { fontWeight: 'fontWeightBold' } }}>
              {record.mercado && (
                <Chip
                  size="small"
                  label={record.mercado}
                  sx={timelineMarketChipSx}
                />
              )}
              {record.relevancia && (
                <Chip
                  size="small"
                  label={`Relevância: ${record.relevancia}`}
                  variant="outlined"
                  sx={timelineNeutralChipSx}
                />
              )}
              {isFutureEvent && (
                <Chip
                  size="small"
                  icon={<AccessTime fontSize="small" />}
                  label="Evento futuro"
                  variant="outlined"
                  sx={timelineFutureChipSx}
                />
              )}
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {isFutureEvent ? (
                <TimelineFutureEventIndicator record={record} />
              ) : (
                record.datareferencia && (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {formatTimelineDate(record.datareferencia)}
                  </Typography>
                )
              )}
              <Tooltip title={favoriteTooltip}>
                <span data-timeline-card-interactive>
                  <IconButton
                    aria-label={favoriteTooltip}
                    aria-pressed={isFavorite}
                    disabled={!canToggleFavorite || isOperationMutating}
                    onClick={handleFavoriteClick}
                    size="small"
                    sx={{ color: 'primary.dark' }}>
                    {isFavorite ? <Star /> : <StarBorder />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
          {!operationScoped &&
            operationName &&
            (operationPath ? (
              <Link
                href={operationPath}
                target="_blank"
                rel="noopener noreferrer"
                data-timeline-card-interactive
                variant="body2"
                underline="hover"
                color="primary.dark"
                fontWeight={700}
                onClick={(event) => event.stopPropagation()}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  alignSelf: 'flex-start',
                  gap: 0.5,
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                }}>
                {operationName}
                <OpenInNewRounded fontSize="small" aria-hidden="true" />
              </Link>
            ) : (
              <Typography
                variant="body2"
                color="primary.dark"
                fontWeight={700}
                sx={{ overflowWrap: 'anywhere' }}>
                {operationName}
              </Typography>
            ))}
          <Stack spacing={1} sx={{ minWidth: 0 }}>
            {title && (
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{
                  display: '-webkit-box',
                  overflow: 'hidden',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                }}>
                {title}
              </Typography>
            )}
            {documentClassification && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                {documentClassification}
              </Typography>
            )}
          </Stack>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              overflow: 'hidden',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
            }}>
            {hasSummary
              ? summary
              : 'Análise documental ainda não disponível para este registro'}
          </Typography>
          <Divider />
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1.5, sm: 1 }}
            justifyContent={secondarySubjects ? 'space-between' : 'flex-end'}
            alignItems={{
              xs: secondarySubjects ? 'stretch' : 'flex-end',
              sm: 'center',
            }}>
            {secondarySubjects && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                {`Assuntos secundários · ${secondarySubjects}`}
              </Typography>
            )}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Button
                variant="text"
                size="small"
                aria-label={'Ver detalhes de ' + (title || 'documento')}
                onClick={() => onOpenDetails(record)}
                sx={{ minWidth: 0, px: 1.5 }}>
                Ver detalhes
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
